import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiShield, FiInfo } from 'react-icons/fi';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { User } from '../../types';
import { LoadingButton } from '../Common/Loader';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSaved?: () => void;
};

function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

export default function GenesisRiskManagementModal({ isOpen, onClose, user, onSaved }: Props) {
  const setUser = useAuthStore((s) => s.setUser);
  const [pctInput, setPctInput] = useState(String(user.genesisRm ?? 100));

  useEffect(() => {
    if (isOpen) {
      setPctInput(String(user.genesisRm ?? 100));
    }
  }, [isOpen, user.genesisRm]);

  const dayBal = Number(user.genesisDayBalance ?? 0);
  const pctNum = parseFloat(pctInput.replace(',', '.'));
  const validPct = Number.isFinite(pctNum) && pctNum >= 1 && pctNum <= 100;

  const calculation = useMemo(() => {
    if (!validPct) return null;
    const p = pctNum;
    if (dayBal > 0) {
      const lossCap = (dayBal * p) / 100;
      const equityFloor = dayBal - lossCap;
      return {
        title: `Your ${p}% limit means:`,
        lines: [
          `Day-start baseline (Genesis): ${formatUsd(dayBal)}.`,
          `Copy trading pauses when today's drawdown reaches ${p}% — about ${formatUsd(lossCap)} below that baseline.`,
          `That is roughly when equity is at or below ${formatUsd(equityFloor)} (UTC day).`,
        ],
      };
    }
    return {
      title: `Your ${p}% limit means:`,
      lines: [
        'Your Genesis day-start balance is set at the start of each UTC day.',
        `Once today's loss reaches ${p}% of that baseline, Genesis copy trading pauses until the next UTC day.`,
      ],
    };
  }, [dayBal, pctNum, validPct]);

  const updateMutation = useApi.useUpdateGenesisRm({
    onSuccess: (data) => {
      if (data.user) setUser(data.user);
      showToast('success', 'Genesis risk limit saved.');
      onSaved?.();
      onClose();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Could not save risk limit.');
    },
  });

  const handleSave = () => {
    if (!validPct) {
      showToast('error', 'Enter a percent between 1 and 100.');
      return;
    }
    updateMutation.mutate(Math.round(pctNum * 100) / 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-3 sm:p-4">
      <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-border">
        <div className="sticky top-0 bg-surface-elevated border-b border-border px-4 sm:px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 shrink-0">
              <FiShield className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground truncate">Genesis Risk Management</h2>
              <p className="text-xs text-muted">Daily loss limit for Genesis copy trades</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-foreground p-1 shrink-0" aria-label="Close">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-lg border border-border bg-surface p-4 flex gap-3">
            <FiInfo className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-foreground space-y-2 min-w-0">
              <p>
                Default is <span className="font-semibold">100%</span>. Lower this to pause copy trading sooner if your
                Genesis account loses that much of today's starting balance.
              </p>
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setPctInput('100')}
              >
                Use 100%
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="genesis-rm-pct" className="block text-sm font-medium text-foreground mb-1.5">
              Daily loss limit (%)
            </label>
            <div className="flex gap-2 items-center">
              <input
                id="genesis-rm-pct"
                type="number"
                min={1}
                max={100}
                step={0.5}
                value={pctInput}
                onChange={(e) => setPctInput(e.target.value)}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border-2 border-border bg-surface text-foreground focus:border-primary focus:outline-none"
              />
              <span className="text-muted text-sm shrink-0">%</span>
            </div>
            <p className="text-xs text-muted mt-1">Between 1% and 100%. Saved as your account setting (genesisRm).</p>
          </div>

          {calculation && (
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-4 space-y-2">
              <p className="text-sm font-semibold text-foreground">{calculation.title}</p>
              <ul className="text-sm text-muted space-y-1.5 list-disc list-inside">
                {calculation.lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border-2 border-border rounded-lg font-semibold text-foreground hover:bg-surface transition-colors"
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <LoadingButton
              type="button"
              onClick={handleSave}
              isLoading={updateMutation.isPending}
              className="flex-1 py-2.5 font-semibold"
              disabled={!validPct}
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
}
